import { action, makeAutoObservable, observable } from "mobx"
import { PagedResultDto } from "@services/dto/pagedResultDto"
import systemConfigurationService, {
  ImportAccountingCodeSettingResult,
} from "@services/system/systemConfigurationService"
import { NotificationMethodEnum } from "@lib/enum"

class SystemConfigurationStore {
  @observable isLoading: boolean = false
  @observable isLoadingDetail: boolean = false
  // Email Server Profile states
  @observable emailSenderProfiles: any[] = []
  @observable currentEmailSenderProfile: any = null
  // SMS provider
  @observable smsSenderProfiles: any[] = []
  @observable currentSmsSenderProfile: any = null
  // Email/SMS Template states
  @observable detailTemplate: any = null
  @observable tableTemplateEmail: PagedResultDto<any> | null = null
  @observable tableTemplateSMS: PagedResultDto<any> | null = null
  @observable templateParameters: any[] = []
  @observable accountingEntity: PagedResultDto<any> | null = null
  @observable listTemplate: PagedResultDto<any> | null = null
  @observable listNumberingConfig: PagedResultDto<any> | null = null
  @observable listModule: any[] = []
  // Print Template states
  @observable tableTemplatePrint: PagedResultDto<any> | null = null

  constructor() {
    makeAutoObservable(this)
  }

  // EMAIL SERVER ACTIONS
  @action
  public async getListEmailSenderProfile(params: any): Promise<any> {
    this.isLoading = true
    try {
      const result = await systemConfigurationService.getListEmailSenderProfile(
        params
      )
      this.emailSenderProfiles = result || []
      return result
    } catch (error) {
      console.error("Error getting email sender profiles:", error)
      throw error
    } finally {
      this.isLoading = false
    }
  }

  @action
  public async getEmailSenderProfile(id: number): Promise<any> {
    this.isLoadingDetail = true
    try {
      const result = await systemConfigurationService.getEmailSenderProfile(id)
      this.currentEmailSenderProfile = result
      return result
    } catch (error) {
      console.error("Error getting email sender profile:", error)
      throw error
    } finally {
      this.isLoadingDetail = false
    }
  }

  @action
  public async createOrUpdateEmailSenderProfile(body: any): Promise<any> {
    this.isLoading = true
    try {
      const result =
        await systemConfigurationService.createOrUpdateEmailSenderProfile(body)
      // Refresh the list after successful create/update
      return result
    } catch (error) {
      console.error("Error creating/updating email sender profile:", error)
      throw error
    } finally {
      this.isLoading = false
    }
  }

  @action
  public async verifySendMailSmtp(body: any): Promise<any> {
    this.isLoading = true
    try {
      const result = await systemConfigurationService.verifySendMailSmtp(body)
      // Refresh the list after successful create/update
      return result
    } catch (error) {
      console.error("Error creating/updating email sender profile:", error)
      throw error
    } finally {
      this.isLoading = false
    }
  }

  // SMS provider

  // EMAIL SERVER ACTIONS
  @action
  public async getListSmsSenderProfile(params: any): Promise<any> {
    this.isLoading = true
    try {
      const result = await systemConfigurationService.getListSmsSenderProfile(
        params
      )
      this.smsSenderProfiles = result || []
      return result
    } catch (error) {
      console.error("Error getting SMS sender profiles:", error)
      throw error
    } finally {
      this.isLoading = false
    }
  }

  @action
  public async getSmsSenderProfile(id: number): Promise<any> {
    this.isLoadingDetail = true
    try {
      const result = await systemConfigurationService.getSmsSenderProfile(id)
      this.currentSmsSenderProfile = result
      return result
    } catch (error) {
      console.error("Error getting SMS sender profile:", error)
      throw error
    } finally {
      this.isLoadingDetail = false
    }
  }

  @action
  public async createOrUpdateSmsSenderProfile(body: any): Promise<any> {
    this.isLoading = true
    try {
      const result =
        await systemConfigurationService.createOrUpdateSmsSenderProfile(body)
      // Refresh the list after successful create/update
      return result
    } catch (error) {
      console.error("Error creating/updating SMS sender profile:", error)
      throw error
    } finally {
      this.isLoading = false
    }
  }

  // EMAIL/SMS/DOCE TEMPLATE ACTIONS
  @action
  public async getAllTemplatesMap(params: any): Promise<PagedResultDto<any>> {
    this.isLoading = true
    try {
      const result = await systemConfigurationService.getAllTemplatesMap(params)
      switch (params.notificationMethod) {
        case NotificationMethodEnum.EMAIL:
          this.tableTemplateEmail = result
          return result
        case NotificationMethodEnum.SMS:
          this.tableTemplateSMS = result
          return result
        case NotificationMethodEnum.DocxEditor:
          this.tableTemplatePrint = result
          return result
        default:
          throw new Error("Invalid notification method")
      }
    } finally {
      this.isLoading = false
    }
  }

  @action
  public async getTemplate(id: number): Promise<any> {
    this.isLoading = true
    try {
      const result = await systemConfigurationService.getGetTemplate(id)
      this.detailTemplate = result
      return result
    } finally {
      this.isLoading = false
    }
  }

  @action
  public async createOrUpdateNotificationTemplate(body: any): Promise<any> {
    this.isLoading = true
    try {
      const result =
        await systemConfigurationService.createOrUpdateNotificationTemplate(
          body
        )
      return result
    } catch (error) {
      console.error("Error creating/updating notification template:", error)
      throw error
    } finally {
      this.isLoading = false
    }
  }

  @action
  public async getParameters(params: any): Promise<any> {
    try {
      const result = await systemConfigurationService.GetParameters(params)
      this.templateParameters = result || []
      return result
    } catch (error) {
      console.error("Error getting parameters:", error)
      throw error
    }
  }

  // UTILITY ACTIONS
  @action
  public clearCurrentEmailSenderProfile(): void {
    this.currentEmailSenderProfile = null
  }
  @action
  public clearCurrentSmsSenderProfile(): void {
    this.currentSmsSenderProfile = null
  }
  @action
  public clearDetailTemplate(): void {
    this.detailTemplate = null
  }
  @action
  public clearTemplateParameters(): void {
    this.templateParameters = []
  }
  
  @action
  public async GetAllAccountingEntity(params: any): Promise<PagedResultDto<any>> {
    this.isLoading = true
    const result = await systemConfigurationService.getAllAccountingEntity(params)
    .finally(() => (this.isLoading = false))
    this.accountingEntity = result
    return result
  }

  @action
  public async UpdateAccountingEntity(body: any): Promise<any> {
    this.isLoading = true
    const result = await systemConfigurationService.updateAccountingEntity(body)
    .finally(() => (this.isLoading = false))
    return result
  }

  @action
  public async ImportAccountingCodeSetting(file: File): Promise<ImportAccountingCodeSettingResult> {
    this.isLoading = true
    const result = await systemConfigurationService
      .importAccountingCodeSetting(file)
      .finally(() => (this.isLoading = false))
    return result
  }

  @action
  public async DownloadAccountingCodeSettingTemplate(): Promise<void> {
    this.isLoading = true
    await systemConfigurationService
      .downloadAccountingCodeSettingTemplate()
      .finally(() => (this.isLoading = false))
  }

  @action
  public async getListTemplate(params: any): Promise<PagedResultDto<any>> {
    this.isLoading = true
    try {
      const result = await systemConfigurationService.getListTemplate(params)
      this.listTemplate = result
      return result
    } catch (error) {
      console.error("Error getting list template:", error)
      throw error
    } finally {
      this.isLoading = false
    }
  }

  @action
  public async cloneTemplate(body: any): Promise<any> {
    this.isLoading = true
    const result = await systemConfigurationService.cloneTemplate(body)
    .finally(() => (this.isLoading = false))
    return result
  }


  @action
  public async getAllNumberingConfig(params: any): Promise<any> {
    this.isLoading = true
    const result = await systemConfigurationService.getAllNumberingConfig(params)
    .finally(() => (this.isLoading = false))
    this.listNumberingConfig = result
    return result
  }

  @action
  public async createOrUpdateNumberingConfig(body: any): Promise<any> {
    this.isLoading = true
    const result = await systemConfigurationService.createOrUpdateNumberingConfig(body)
    .finally(() => (this.isLoading = false))
    return result
  }

  /**
   * Sửa số hiện tại của một dòng bộ đếm. Backend trả về nguyên dòng sau khi ghi, nên vá tại chỗ
   * `listNumberingConfig` thay vì gọi lại `GetAll` — người dùng đang ở đúng trang đó.
   */
  @action
  public async updateNumberingConfigLastNumber(body: {
    id: number
    lastNumber: number
  }): Promise<any> {
    this.isLoading = true
    const result = await systemConfigurationService
      .updateNumberingConfigLastNumber(body)
      .finally(() => (this.isLoading = false))

    if (result) {
      const list: any = this.listNumberingConfig
      const patch = (items: any[]) =>
        items.map((item: any) =>
          item?.id === result.id ? { ...item, ...result } : item
        )

      if (Array.isArray(list)) {
        this.listNumberingConfig = patch(list) as any
      } else if (Array.isArray(list?.items)) {
        this.listNumberingConfig = {
          ...list,
          items: patch(list.items),
        } as PagedResultDto<any>
      }
    }

    return result
  }

  @action
  public async getListModule(): Promise<any> {
    this.isLoading = true
    const result = await systemConfigurationService.getListModule()
    .finally(() => (this.isLoading = false))
    this.listModule = result
    return result
  }

  @action
  public async sendMailSmtp(body: any): Promise<any> {
    this.isLoading = true
    const result = await systemConfigurationService.sendMailSmtp(body)
    .finally(() => (this.isLoading = false))
    return result
  }
}
export default SystemConfigurationStore
